import { Injectable } from '@angular/core';

export interface OrderPdfData {
  campaignName: string;        // forma wyświetlana w treści, np. 'KLIMATYZACJA'
  campaignNameTitle: string;   // forma do tytułu i nazwy pliku, np. 'klimatyzacje'
  orderDate: string;   // sformatowana data (strefa Warszawa)
  userEmail: string;
  // Wybrany Paczkomat InPost (model z zamówienia). null dla starszych zamówień
  // złożonych zanim wybór Paczkomatu stał się obowiązkowy.
  parcelLocker?: { name: string; address: string; city: string; postCode: string } | null;
  total: number;
  items: Array<{ name: string; unitPrice: number; quantity: number; lineTotal: number }>;
}

@Injectable({
  providedIn: 'root'
})
export class OrderPdfService {

  // pdfmake ładowany leniwie (dynamic import) — trafia do osobnego chunku,
  // nie obciąża initial bundla i wczytuje się dopiero przy generowaniu PDF.
  // Roboto (z vfs_fonts) poprawnie renderuje polskie znaki.
  public async downloadOrderPdf(data: OrderPdfData): Promise<void> {
    const pdfMakeModule: any = await import('pdfmake/build/pdfmake');
    const pdfFontsModule: any = await import('pdfmake/build/vfs_fonts');
    const pdfMake: any = pdfMakeModule.default ?? pdfMakeModule;
    const pdfFonts: any = pdfFontsModule.default ?? pdfFontsModule;
    pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs;

    pdfMake.createPdf(this.buildDocDefinition(data)).download(`pana-store-zamowienie-${data.campaignNameTitle}.pdf`);
  }

  // Składa czytelny adres Paczkomatu z dostępnych pól: "ulica nr, kod miasto".
  // Pomija puste fragmenty, by nie zostawiać wiszących przecinków/spacji.
  private formatParcelLockerAddress(locker: OrderPdfData['parcelLocker']): string {
    if (!locker) {
      return '';
    }
    const cityLine = [locker.postCode, locker.city].filter(Boolean).join(' ').trim();
    return [locker.address, cityLine].filter(Boolean).join(', ').trim();
  }

  private buildDocDefinition(data: OrderPdfData): any {
    const tableBody: any[] = [
      [
        { text: 'Nagroda', bold: true },
        { text: 'Cena jedn.', bold: true, alignment: 'right' },
        { text: 'Ilość', bold: true, alignment: 'right' },
        { text: 'Wartość', bold: true, alignment: 'right' }
      ],
      ...data.items.map(item => [
        item.name,
        { text: `${item.unitPrice} zł`, alignment: 'right' },
        { text: String(item.quantity), alignment: 'right' },
        { text: `${item.lineTotal} zł`, alignment: 'right' }
      ])
    ];

    // Blok Paczkomatu — między adresem e-mail a tabelą benefitów. Renderowany
    // tylko gdy zamówienie ma wybrany Paczkomat (starsze zamówienia go nie mają).
    const lockerAddress = this.formatParcelLockerAddress(data.parcelLocker);
    const hasLocker = !!data.parcelLocker?.name;
    const parcelLockerBlock: any[] = hasLocker
      ? [
          { text: `Paczkomat InPost: ${data.parcelLocker!.name}`, margin: [0, 0, 0, lockerAddress ? 2 : 12] },
          ...(lockerAddress ? [{ text: `Adres Paczkomatu: ${lockerAddress}`, margin: [0, 0, 0, 12] }] : [])
        ]
      : [];

    return {
      content: [
        { text: `Zamówienie Pana Store`, style: 'header' },
        { text: `Kampania: ${data.campaignName}`, margin: [0, 0, 0, 2] },
        { text: `Data zamówienia: ${data.orderDate}`, margin: [0, 0, 0, 2] },
        { text: `E-mail: ${data.userEmail}`, margin: [0, 0, 0, hasLocker ? 2 : 12] },
        ...parcelLockerBlock,
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: tableBody
          },
          layout: 'lightHorizontalLines'
        },
        { text: `Łączna wartość: ${data.total} zł`, style: 'total', margin: [0, 14, 0, 0] }
      ],
      styles: {
        header: { fontSize: 18, bold: true, margin: [0, 0, 0, 12] },
        total: { fontSize: 14, bold: true, alignment: 'right' }
      },
      defaultStyle: { fontSize: 10 }
    };
  }
}
